import { CartAddEvent } from '@theme/events';
import { pushAddToCart } from '@theme/data-layer-helper';

class AddToCartOverlay extends HTMLElement {
  constructor() {
    super();
    this.selectedOptions = {};
    this.optionsRendered = false;
    this.eventListenersAttached = false;
  }

  connectedCallback() {
    // Query DOM elements fresh each time the element is connected
    this.overlay = this.querySelector('[data-overlay]');
    this.form = this.querySelector('form');
    this.closeButton = this.querySelector('[data-close-overlay]');
    this.optionsContainer = this.querySelector('[data-options-container]');
    this.addToCartButton = this.querySelector('[data-add-to-cart-button]');
    
    // Find the open button in the parent container
    const parentContainer = this.closest('.group');
    if (parentContainer) {
      this.openButton = parentContainer.querySelector('[data-open-overlay]');
    }
    
    if (!this.overlay || !this.form) return;
    
    // Store reference to template ID
    this.templateId = this.overlay.id + '-options';
    this.variants = null; // Will be loaded when overlay opens
    this.metafields = null; // Will be loaded when overlay opens
    
    // Reset the flag when reconnected
    this.optionsRendered = false;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.openButton) {
      this.openButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openOverlay();
      });
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        this.closeOverlay();
      });
    }

    // Global event listeners for form submission and button clicks
    const handleGlobalSubmit = (e) => {
      const target = e.target;
      
      // Only intercept form submissions, not option clicks
      if (e.type === 'submit' && (target === this.form || target.closest('add-to-cart-overlay') === this)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const button = target.querySelector('[data-add-to-cart-button]');
        const buttonText = button?.querySelector('[data-button-text]');
        if (buttonText) buttonText.classList.add('hidden');
        const loadingSpinner = button?.querySelector('[data-loading-spinner]');
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');
        this.handleAddToCart();
        return false;
      }
      
      // Only intercept clicks on the submit button, not option buttons
      if (e.type === 'click' && (target === this.addToCartButton || target.closest('[data-add-to-cart-button]') === this.addToCartButton)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const button = target === this.addToCartButton ? target : target.closest('[data-add-to-cart-button]');
        const buttonText = button?.querySelector('[data-button-text]');
        if (buttonText) buttonText.classList.add('hidden');
        const loadingSpinner = button?.querySelector('[data-loading-spinner]');
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');
        this.handleAddToCart();
        return false;
      }
    };

    document.addEventListener('submit', handleGlobalSubmit, true);
    document.addEventListener('click', handleGlobalSubmit, true);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.overlay.classList.contains('hidden')) {
        this.closeOverlay();
      }
    });
  }

  setupOptionListeners() {
    const optionButtons = this.querySelectorAll('.product-options-item');
    optionButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleOptionSelect(button);
      });
    });
  }

  renderOptions() {    
    // Reset optionsRendered if container is empty (after successful add to cart)
    if (this.optionsRendered && this.optionsContainer && this.optionsContainer.children.length === 0) {
     this.optionsRendered = false;
    }
    
    if (this.optionsRendered || !this.optionsContainer) {
      return;
    }
    
    const template = document.getElementById(this.templateId);
    if (!template) return;
    
    // Clone template content and append to container
    const content = template.content.cloneNode(true);
    this.optionsContainer.appendChild(content);
    
    // Setup event listeners for the newly rendered options
    this.setupOptionListeners();
    
    // Initialize selected options
    const optionGroups = this.querySelectorAll('.product-options');
    optionGroups.forEach(group => {
      const selectedButton = group.querySelector('[aria-checked="true"]');
      if (selectedButton) {
        const optionName = group.dataset.optionName;
        const optionValue = selectedButton.dataset.optionValue;
        this.selectedOptions[optionName] = optionValue;
      }
    });
    
    this.optionsRendered = true;
  }

  initializeVariantIds() {
    if (!this.variants || this.variants.length === 0) return;

    // Find the currently selected width
    const optionGroups = this.querySelectorAll('.product-options');
    let selectedWidth = null;
    let widthOptionPosition = -1;
    let sizeOptionPosition = -1;

    optionGroups.forEach(group => {
      const optionName = group.dataset.optionName.toLowerCase();
      const position = parseInt(group.dataset.optionPosition) - 1;
      
      if (optionName.includes('width')) {
        widthOptionPosition = position;
        const selectedWidthButton = group.querySelector('[aria-checked="true"]');
        if (selectedWidthButton) {
          selectedWidth = selectedWidthButton.dataset.optionValue;
        }
      } else if (optionName.includes('size')) {
        sizeOptionPosition = position;
      }
    });

    // If we have a width selected, update size options for that width
    if (selectedWidth && sizeOptionPosition >= 0) {
      this.updateSizeOptionsForWidth(selectedWidth);
    } else if (sizeOptionPosition >= 0) {
      // No width option exists, just set variant IDs for size options
      const sizeGroup = Array.from(optionGroups).find(group => {
        const optionName = group.dataset.optionName.toLowerCase();
        return optionName.includes('size');
      });

      if (sizeGroup) {
        const sizeButtons = sizeGroup.querySelectorAll('.product-options-item');
        sizeButtons.forEach(sizeButton => {
          const sizeValue = sizeButton.dataset.optionValue;
          
          // Find matching variant for this size
          for (const variant of this.variants) {
            const variantSize = variant.options[sizeOptionPosition];
            if (variantSize === sizeValue) {
              sizeButton.dataset.variantId = variant.id;
              
              if (!variant.available) {
                sizeButton.disabled = true;
                sizeButton.classList.add('opacity-50', 'cursor-not-allowed', 'strikethrough-diagonal');
              }
              break;
            }
          }
        });
      }
    }
  }

  handleOptionSelect(button) {
    if (button.disabled) return;

    const optionGroup = button.closest('.product-options');
    const optionName = optionGroup.dataset.optionName;
    const optionValue = button.dataset.optionValue;
    const optionPosition = parseInt(optionGroup.dataset.optionPosition) - 1;

    const allButtonsInGroup = optionGroup.querySelectorAll('.product-options-item');
    allButtonsInGroup.forEach(btn => {
      btn.setAttribute('aria-checked', 'false');
      btn.classList.remove('border-2', 'border-toms-indigo', 'font-semibold');
      btn.classList.add('border-toms-ui-stroke');
      const span = btn.querySelector('span');
      if (span) {
        span.classList.remove('underline', 'underline-offset-4', 'font-bold');
      }
    });

    button.setAttribute('aria-checked', 'true');
    
    const isWidthOption = optionGroup.querySelector('.last\\:border-l') !== null;
    if (isWidthOption) {
      const span = button.querySelector('span');
      if (span) {
        span.classList.add('underline', 'underline-offset-4', 'font-bold');
      }
    } else {
      button.classList.remove('border-toms-ui-stroke');
      button.classList.add('border-2', 'border-toms-indigo', 'font-semibold');
    }

    this.selectedOptions[optionName] = optionValue;

    // If this is a width option, update the size options based on selected width
    if (isWidthOption) {
      this.updateSizeOptionsForWidth(optionValue);
    } else {
      // This is a size option - find the correct variant ID based on selected width + size
      const variantId = this.findVariantIdForSelection();
      if (variantId) {
        this.updateVariantInput(variantId);
      }
    }

    this.updateAddToCartButton(button);
  }

  updateVariantInput(variantId) {
    const variantInput = this.form.querySelector('[data-variant-input]');
    if (variantInput && variantId) {
      variantInput.value = variantId;
    }
  }

  findVariantIdForSelection() {
    if (!this.variants || this.variants.length === 0) return null;

    // Get all option groups to build the selection
    const optionGroups = this.querySelectorAll('.product-options');
    const selectedOptionValues = [];

    optionGroups.forEach(group => {
      const position = parseInt(group.dataset.optionPosition) - 1;
      const selectedButton = group.querySelector('[aria-checked="true"]');
      if (selectedButton) {
        selectedOptionValues[position] = selectedButton.dataset.optionValue;
      }
    });

    // Find the variant that matches all selected options
    for (const variant of this.variants) {
      let matches = true;
      for (let i = 0; i < selectedOptionValues.length; i++) {
        if (selectedOptionValues[i] && variant.options[i] !== selectedOptionValues[i]) {
          matches = false;
          break;
        }
      }
      if (matches) {
        return variant.id;
      }
    }

    return null;
  }

  updateSizeOptionsForWidth(selectedWidth) {
    if (!this.variants || this.variants.length === 0) return;

    // Find all option groups
    const optionGroups = this.querySelectorAll('.product-options');
    let widthOptionPosition = -1;
    let sizeOptionPosition = -1;

    // Identify which position is width and which is size
    optionGroups.forEach(group => {
      const optionName = group.dataset.optionName.toLowerCase();
      const position = parseInt(group.dataset.optionPosition) - 1;
      
      if (optionName.includes('width')) {
        widthOptionPosition = position;
      } else if (optionName.includes('size')) {
        sizeOptionPosition = position;
      }
    });

    if (sizeOptionPosition === -1) return; // No size option to update

    // Get the size option group
    const sizeGroup = Array.from(optionGroups).find(group => {
      const optionName = group.dataset.optionName.toLowerCase();
      return optionName.includes('size');
    });

    if (!sizeGroup) return;

    // Get all size buttons
    const sizeButtons = sizeGroup.querySelectorAll('.product-options-item');
    
    // Track which size was previously selected
    const previouslySelectedSize = sizeGroup.querySelector('[aria-checked="true"]');
    const previousSizeValue = previouslySelectedSize?.dataset.optionValue;

    let firstAvailableButton = null;
    let previousSizeStillAvailable = false;

    // Update each size button based on variant availability for the selected width
    sizeButtons.forEach(sizeButton => {
      const sizeValue = sizeButton.dataset.optionValue;
      
      // Find if there's an available variant for this width + size combination
      let hasAvailableVariant = false;
      let matchingVariantId = null;

      for (const variant of this.variants) {
        const variantWidth = widthOptionPosition >= 0 ? variant.options[widthOptionPosition] : null;
        const variantSize = variant.options[sizeOptionPosition];

        // Check if this variant matches the selected width and current size
        if (variantSize === sizeValue && variantWidth === selectedWidth) {
          matchingVariantId = variant.id;
          if (variant.available) {
            hasAvailableVariant = true;
            break;
          }
        }
      }

      // Update button state
      if (matchingVariantId) {
        sizeButton.dataset.variantId = matchingVariantId;
        
        if (hasAvailableVariant) {
          sizeButton.disabled = false;
          sizeButton.classList.remove('opacity-50', 'cursor-not-allowed', 'strikethrough-diagonal');
          
          // Track first available button
          if (!firstAvailableButton) {
            firstAvailableButton = sizeButton;
          }
          
          // Check if previously selected size is still available
          if (sizeValue === previousSizeValue) {
            previousSizeStillAvailable = true;
          }
        } else {
          sizeButton.disabled = true;
          sizeButton.classList.add('opacity-50', 'cursor-not-allowed', 'strikethrough-diagonal');
        }
      }
      
      // Reset selection state for all buttons
      sizeButton.setAttribute('aria-checked', 'false');
      sizeButton.classList.remove('border-2', 'border-toms-indigo', 'font-semibold');
      sizeButton.classList.add('border-toms-ui-stroke');
    });

    // Auto-select a size: prefer previously selected if still available, otherwise first available
    let buttonToSelect = null;
    if (previousSizeStillAvailable && previouslySelectedSize) {
      buttonToSelect = previouslySelectedSize;
    } else if (firstAvailableButton) {
      buttonToSelect = firstAvailableButton;
    }

    if (buttonToSelect) {
      // Update visual state
      buttonToSelect.setAttribute('aria-checked', 'true');
      buttonToSelect.classList.remove('border-toms-ui-stroke');
      buttonToSelect.classList.add('border-2', 'border-toms-indigo', 'font-semibold');
      
      // Update variant input
      if (buttonToSelect.dataset.variantId) {
        this.updateVariantInput(buttonToSelect.dataset.variantId);
      }
      
      // Update add to cart button
      this.updateAddToCartButton(buttonToSelect);
    }
  }

  updateAddToCartButton(selectedButton) {
    const buttonText = this.addToCartButton?.querySelector('[data-button-text]');
    
    if (!this.addToCartButton) return;
    
    const isAvailable = !selectedButton.disabled;
    
    if (isAvailable) {
      this.addToCartButton.disabled = false;
      if (buttonText) {
        buttonText.textContent = buttonText.dataset.availableText || 'Add to cart';
      }
    } else {
      this.addToCartButton.disabled = true;
      if (buttonText) {
        buttonText.textContent = buttonText.dataset.soldOutText || 'Sold out';
      }
    }
  }

  async handleAddToCart() {
    const addToCartButton = this.addToCartButton;
    const buttonText = addToCartButton?.querySelector('[data-button-text]');
    const loadingSpinner = addToCartButton?.querySelector('[data-loading-spinner]');
    const statusMessage = this.querySelector('[data-status-message]');
    if (addToCartButton) addToCartButton.disabled = true;
    try {
      const formData = new FormData(this.form);
      
      // Add _LINE_CHECKOUT_DATA property with item data for checkout analytics
      const variantInput = this.form.querySelector('input[name="id"]');
      if (variantInput?.value) {
        const lineCheckoutData = this.buildLineCheckoutData(variantInput.value);
        if (lineCheckoutData) {
          formData.append('properties[_LINE_CHECKOUT_DATA]', JSON.stringify(lineCheckoutData));
        }
      }
      
      // Add sections parameter to get updated cart HTML
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });
      if (cartItemComponentsSectionIds.length > 0) {
        formData.append('sections', cartItemComponentsSectionIds.join(','));
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      // Check if Shopify returned an error in the response
      if (data.status || !response.ok) {
        const errorMessage = data.message || data.description || 'Failed to add to cart';
        if (statusMessage) {
          statusMessage.textContent = errorMessage;
          statusMessage.classList.remove('hidden');
        }
        
        if (buttonText) buttonText.classList.remove('hidden');
        if (loadingSpinner) loadingSpinner.classList.add('hidden');
        if (addToCartButton) addToCartButton.disabled = false;

        // Mirror the PDP add-to-cart error UX: close the overlay, auto-open the
        // cart drawer, and dispatch a CartAddEvent so cart-items-component can
        // surface the cart-limit banner inside the drawer.
        const variantIdInputErr = this.form.querySelector('input[name="id"]');
        const quantityInputErr = this.form.querySelector('input[name="quantity"]');
        this.closeOverlay();

        const cartDrawerErr = document.querySelector('cart-drawer-component');
        const hadAutoOpenErr = cartDrawerErr?.hasAttribute('auto-open');
        if (cartDrawerErr && !hadAutoOpenErr) {
          cartDrawerErr.setAttribute('auto-open', '');
        }

        document.dispatchEvent(
          new CartAddEvent({}, variantIdInputErr?.value || '', {
            didError: true,
            source: 'add-to-cart-overlay',
            itemCount: Number(quantityInputErr?.value) || 1,
          })
        );

        if (cartDrawerErr && !hadAutoOpenErr) {
          setTimeout(() => {
              cartDrawerErr.removeAttribute('auto-open');
            }, 500);
          }

        return;
      }

      if (statusMessage) {
        statusMessage.textContent = 'Added to cart';
      }

      // Reset button state
      if (buttonText) buttonText.classList.remove('hidden');
      if (loadingSpinner) loadingSpinner.classList.add('hidden');
      if (addToCartButton) addToCartButton.disabled = false;

      // Get form data before closing overlay
      const variantIdInput = this.form.querySelector('input[name="id"]');
      const quantityInput = this.form.querySelector('input[name="quantity"]');

      // Close overlay
      this.closeOverlay();

      // Find cart drawer and set auto-open attribute
      const cartDrawer = document.querySelector('cart-drawer-component');
      
      const hadAutoOpen = cartDrawer?.hasAttribute('auto-open');
      
      if (cartDrawer && !hadAutoOpen) {
        cartDrawer.setAttribute('auto-open', '');
      }

      // Get metafields from data attribute on the add-to-cart button
      let metafields = {};
      try {
        if (this.metafields) {
          metafields = { ...this.metafields };
        }
      } catch (e) {
        console.error('Failed to parse product metafields:', e);
      }

      // Add selected variant's price and compare_at_price to metafields
      const selectedVariantId = variantIdInput?.value;
      if (selectedVariantId && this.variants) {
        const selectedVariant = this.variants.find(v => String(v.id) === String(selectedVariantId));
        if (selectedVariant) {
          metafields.variant_price = selectedVariant.price || 0;
          metafields.variant_compare_at_price = selectedVariant.compare_at_price || 0;
        }
      }

      // Push to dataLayer using cart response and metafields
      pushAddToCart(data, metafields);

      // Small delay to ensure attribute is set before dispatching event
      setTimeout(() => {
        // Dispatch the cart add event using CartAddEvent class (same as product-form-component)
        if (variantIdInput) {
          document.dispatchEvent(
            new CartAddEvent({}, variantIdInput.value, {
              source: 'add-to-cart-overlay',
              itemCount: Number(quantityInput?.value) || 1,
              sections: data.sections
            })
          );
        }

        // Remove auto-open attribute after drawer should have opened
        if (cartDrawer && !hadAutoOpen) {
          setTimeout(() => {
            cartDrawer.removeAttribute('auto-open');
          }, 500);
        }
      }, 50);

    } catch (error) {
      console.error('Error adding to cart:', error);
      if (statusMessage) {
        statusMessage.textContent = 'Error adding to cart. Please try again.';
        statusMessage.classList.remove('hidden');
      }
      
      if (buttonText) buttonText.classList.remove('hidden');
      if (loadingSpinner) loadingSpinner.classList.add('hidden');
      if (addToCartButton) addToCartButton.disabled = false;
    }
  }

  closeAllOtherOverlays() {
    // Find all other add-to-cart-overlay instances
    const allOverlays = document.querySelectorAll('add-to-cart-overlay');
    
    allOverlays.forEach(overlay => {
      if (overlay !== this) {
        const overlayElement = overlay.querySelector('[data-overlay]');
        if (overlayElement && !overlayElement.classList.contains('hidden')) {
          overlayElement.classList.add('hidden');
          overlayElement.classList.remove('flex');
        }
      }
    });
  }

  openOverlay() {
    if (!this.overlay) return;
    
    // Load product data only when opening (lazy loading)
    this.loadProductData();
    
    // Render options only once
    this.renderOptions();
    
    // Initialize variant IDs on size buttons
    this.initializeVariantIds();
    
    this.overlay.classList.remove('hidden');
    this.overlay.classList.add('flex');
    this.trapFocus();
  }

  loadProductData() {
    if (this.variants !== null) return; // Data already loaded
    if (!this.overlay) return;

    try {
      const scriptTag = this.overlay.querySelector('script[product-data]');
      if (scriptTag) {
        const data = JSON.parse(scriptTag.textContent || '{}');
        this.variants = data.variants || [];
        this.metafields = data.metafields || {};
        // Store product-level data for _LINE_CHECKOUT_DATA
        this.productId = data.product_id || '';
        this.productTitle = data.product_title || '';
        this.productVendor = data.product_vendor || '';
        this.productUrl = data.product_url || '';
        this.productImage = data.product_image || '';
      }
    } catch (e) {
      console.error('Failed to parse product data:', e);
      this.variants = [];
      this.metafields = {};
    }
  }

  buildLineCheckoutData(variantId) {
    if (!this.variants || !this.metafields) return null;

    const selectedVariant = this.variants.find(v => String(v.id) === String(variantId));
    if (!selectedVariant) return null;

    const price = (selectedVariant.price || 0) / 100;
    const compareAtPrice = selectedVariant.compare_at_price || selectedVariant.price || 0;
    const msrp = compareAtPrice > selectedVariant.price ? compareAtPrice / 100 : price;
    const msrpMarkdown = msrp - price;

    return {
      item_id: String(this.productId),
      item_name: this.productTitle,
      item_variant: String(selectedVariant.id),
      item_brand: this.productVendor,
      price: String(price),
      msrp: String(msrp),
      msrp_markdown: String(msrpMarkdown),
      compare_at_price: String((selectedVariant.compare_at_price || 0) / 100),
      item_category: this.metafields.gender || '',
      item_category2: this.metafields.category || '',
      item_category3: this.metafields.refinement_color || '',
      item_category4: this.metafields.class || '',
      item_category5: this.metafields.material_short_id || '',
      item_type: this.metafields.category || '',
      item_color: this.metafields.refinement_color || '',
      item_gender: this.metafields.gender || '',
      item_size: selectedVariant.title || '',
      material: this.metafields.material_description || '',
      quantity: '1',
      affiliation: 'online'
    };
  }

  closeOverlay() {
    if (this.overlay) {
      this.overlay.classList.add('hidden');
      this.overlay.classList.remove('flex');
    }
  }

  trapFocus() {
    const focusableElements = this.overlay.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    firstElement.focus();
    
    this.overlay.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });
  }

  disconnectedCallback() {
    // Cleanup if needed
  }
}

customElements.define('add-to-cart-overlay', AddToCartOverlay);